import { useTTV } from "@/hooks/useLive";
import type { TTVRow } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { cn } from "@/lib/utils";

function Tick({ v }: { v?: boolean | null }) {
  if (v === null || v === undefined) return <span className="text-muted-foreground">—</span>;
  return v ? (
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 font-semibold">✓</span>
  ) : (
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground">·</span>
  );
}

function Cell({ value }: { value: number | null | undefined | string }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">—</span>;
  }
  return <span className="tabular">{value}</span>;
}

function CohortTable({ cohort, accent, rows }: { cohort: "A" | "B"; accent: string; rows: TTVRow[] }) {
  return (
    <div className="space-y-3">
      <div
        className={cn(
          "inline-flex items-center gap-2 rounded-md px-3 py-1 text-caption font-semibold",
          accent
        )}
      >
        Cohort {cohort} — {cohort === "A" ? "High-Touch CS" : "Self-Serve"} · {rows.length} accounts
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[1100px] text-caption">
          <thead className="bg-muted/50">
            <tr className="text-left text-micro uppercase tracking-wider text-muted-foreground">
              {[
                "Account",
                "Seg.",
                "Phase",
                "Handoffs 14d",
                "/1K",
                "TTV1",
                "Appts",
                "TTV2",
                "Days→Appt",
                "Listings",
                "TTV3",
                "Days→List",
                "Sold",
                "TTV4",
                "Days→Sale",
              ].map((h) => (
                <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <tr key={row.name} className="hover:bg-muted/30" title={row.notes}>
                <td className="whitespace-nowrap px-3 py-2 font-medium text-foreground">{row.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{row.segment}</td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{row.phase ?? "—"}</td>
                <td className="px-3 py-2"><Cell value={row.handoffs14d} /></td>
                <td className="px-3 py-2"><Cell value={row.handoffsPer1k} /></td>
                <td className="px-3 py-2"><Tick v={row.ttv1} /></td>
                <td className="px-3 py-2"><Cell value={row.listingAppts} /></td>
                <td className="px-3 py-2"><Tick v={row.ttv2} /></td>
                <td className="px-3 py-2"><Cell value={row.daysToFirstAppt} /></td>
                <td className="px-3 py-2"><Cell value={row.listings} /></td>
                <td className="px-3 py-2"><Tick v={row.ttv3} /></td>
                <td className="px-3 py-2"><Cell value={row.daysToFirstListing} /></td>
                <td className="px-3 py-2"><Cell value={row.sold} /></td>
                <td className="px-3 py-2"><Tick v={row.ttv4} /></td>
                <td className="px-3 py-2"><Cell value={row.daysToFirstSale} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function hitRate(rows: TTVRow[], key: "ttv1" | "ttv2" | "ttv3" | "ttv4"): string {
  const evaluated = rows.filter((r) => r[key] !== null && r[key] !== undefined);
  if (evaluated.length === 0) return "—";
  const hit = evaluated.filter((r) => r[key] === true).length;
  return `${Math.round((hit / evaluated.length) * 100)}%`;
}

export function TTVPanel() {
  const TTV_DATA = useTTV();
  const all = [...TTV_DATA.cohortA, ...TTV_DATA.cohortB];
  const summary = [
    { label: "Cohort A · TTV1", value: hitRate(TTV_DATA.cohortA, "ttv1"), target: "≥ 70%", accent: "text-emerald-700" },
    { label: "Cohort B · TTV1", value: hitRate(TTV_DATA.cohortB, "ttv1"), target: "≥ 70%", accent: "text-amber-700" },
    { label: "All beta · TTV1", value: hitRate(all, "ttv1"), target: "≥ 70%", accent: "text-sky-700" },
    { label: "All beta · TTV2", value: hitRate(all, "ttv2"), target: "monitor", accent: "text-muted-foreground" },
    { label: "All beta · TTV3", value: hitRate(all, "ttv3"), target: "monitor", accent: "text-muted-foreground" },
    { label: "All beta · TTV4", value: hitRate(all, "ttv4"), target: "monitor", accent: "text-muted-foreground" },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>TTV metrics — beta cohorts</CardTitle>
        <p className="text-caption text-muted-foreground">
          TTV1: 10 handoffs/14d OR 1/1K contacts · TTV2: 1 listing appt/14d · TTV3: 1 listing on
          market/30d · TTV4: 1 listing sold/90d · Target: 70% hit TTV1
        </p>
      </CardHeader>
      <CardContent className="space-y-8">
        <CohortTable cohort="A" accent="bg-emerald-50 text-emerald-700" rows={TTV_DATA.cohortA} />
        <CohortTable cohort="B" accent="bg-amber-50 text-amber-700" rows={TTV_DATA.cohortB} />

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {summary.map((s) => (
            <div key={s.label} className="rounded-lg border border-border bg-muted/30 p-4 text-center">
              <div className="text-micro uppercase tracking-wider text-muted-foreground">{s.label}</div>
              <div className={cn("mt-1 text-h3 font-semibold tabular", s.accent)}>{s.value}</div>
              <div className="text-micro text-muted-foreground">{s.target}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
